import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { RefreshButton } from '@/components/core/refresh-button/refresh-button';
import { useActiveOrganization } from '@/graphql/queries/organizations';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { ContractAnalysesList } from '../components/contract-analyses-list';

/**
 * Org route: `/org/<slug>/contract-checker`
 *
 * Lists past contract analyses for the active org. The "New Analysis"
 * CTA routes to `/contract-checker/new`.
 */
const OrgContractCheckerPage = () => {
  const { t } = useTranslation();
  const { orgId: slug } = useParams();
  const orgId = useCurrentOrgId();
  const { data: org, isLoading } = useActiveOrganization();

  if (isLoading) return <Skeleton className="h-150 w-full" />;
  if (!org || !slug || !orgId) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[hsl(var(--fg))] font-heading">
            {t('ORGCONTRACTCHECKER_LIST_TITLE', { defaultValue: 'Contract Checker' })}
          </h2>
          <p className="text-sm text-globe-grey mt-1">
            {t('ORGCONTRACTCHECKER_LIST_DESCRIPTION', {
              defaultValue: 'Review past analyses or start a new one.',
            })}
          </p>
        </div>
        {/* Desktop-only — on mobile the same button lives in the
            OrgShell topbar so it stays accessible while eliminating the
            duplicate inside the page. */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <RefreshButton
            queryKey={['contractAnalyses']}
            label={t('ORGTEAM_HEADER_REFRESH', { defaultValue: 'Refresh' })}
          />
          <Button asChild variant="primary" size="sm">
            <Link
              to={`/org/${slug}/contract-checker/new`}
              className="inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('ORGCONTRACTCHECKER_LIST_NEW', { defaultValue: 'New Analysis' })}
            </Link>
          </Button>
        </div>
      </div>

      <ContractAnalysesList orgId={orgId} slug={slug} />
    </div>
  );
};

export default OrgContractCheckerPage;
